import { useQuery } from "@tanstack/react-query";
import { CircleAlert, Paperclip, SquarePen } from "lucide-react";
import { Link } from "raviger";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { CardListSkeleton } from "@/components/Common/SkeletonLoading";
import { HospitalizationDetailsDialog } from "@/components/Encounter/HospitalizationDetailsDialog";

import { useEncounter } from "@/pages/Encounters/utils/EncounterProvider";
import { isHospitalizationArrivalComplete } from "@/types/emr/encounter/encounter";
import { FileCategory, FileType } from "@/types/files/file";
import fileApi from "@/types/files/fileApi";
import query from "@/Utils/request/query";

const formatHospitalizationDateTime = (value?: string) =>
  value
    ? new Date(value).toLocaleString([], {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "--";

export const HospitalizationDetails = () => {
  const { t } = useTranslation();
  const {
    selectedEncounter: encounter,
    selectedEncounterId: encounterId,
    patientId,
    facilityId,
    canWriteSelectedEncounter,
  } = useEncounter();
  const [arrivalDialogOpen, setArrivalDialogOpen] = useState(false);
  const isInpatient = encounter?.encounter_class === "imp";

  const { data: referralLetters } = useQuery({
    queryKey: [
      "files",
      FileType.ENCOUNTER,
      encounterId,
      FileCategory.REFERRAL_LETTER,
    ],
    queryFn: query(fileApi.list, {
      queryParams: {
        file_type: FileType.ENCOUNTER,
        associating_id: encounterId,
        file_category: FileCategory.REFERRAL_LETTER,
      },
    }),
    enabled: Boolean(encounter && isInpatient),
  });

  if (!encounter) return <CardListSkeleton count={1} />;

  const hasHospitalization =
    encounter.hospitalization?.admit_source ||
    encounter.hospitalization?.arrival_mode ||
    encounter.hospitalization?.arrival_time ||
    encounter.hospitalization?.admission_time ||
    encounter.hospitalization?.expected_discharge_date ||
    encounter.hospitalization?.medico_legal_case !== undefined ||
    encounter.hospitalization?.diet_preference ||
    encounter.hospitalization?.re_admission;
  const hasCompleteArrivalDetails = isHospitalizationArrivalComplete(
    encounter.hospitalization,
  );

  if (!isInpatient && !hasHospitalization) return null;

  return (
    <div className="bg-gray-100 rounded-md w-full border border-gray-200 pt-2 p-1 space-y-1 dark:bg-gray-900 dark:border-gray-800">
      <div className="flex justify-between items-center text-gray-950 pl-2 dark:text-gray-50">
        <span className="font-semibold">{t("hospitalization_details")}</span>
        {canWriteSelectedEncounter && (
          <Button variant="ghost" size="sm" asChild>
            <Link
              href={`/facility/${facilityId}/patient/${patientId}/encounter/${encounterId}/questionnaire/encounter`}
            >
              <SquarePen className="size-4 cursor-pointer" strokeWidth={1.5} />
            </Link>
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-2 bg-white rounded-md shadow p-2 dark:bg-gray-950">
        {isInpatient && !hasCompleteArrivalDetails && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-dashed border-amber-200 bg-amber-50/40 p-2">
            <div className="flex min-w-0 items-start gap-2">
              <CircleAlert className="mt-0.5 size-4 shrink-0 text-amber-700" />
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-900 dark:text-gray-50">
                  {t("arrival_details_incomplete")}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {t("arrival_details_incomplete_description")}
                </p>
              </div>
            </div>
            {canWriteSelectedEncounter && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 bg-white dark:bg-gray-950"
                onClick={() => setArrivalDialogOpen(true)}
              >
                {t("add_details")}
              </Button>
            )}
          </div>
        )}

        {isInpatient && encounter.hospitalization?.arrival_mode && (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-2 dark:border-gray-800 dark:bg-gray-900">
            <p className="mb-2 text-sm font-semibold text-gray-950 dark:text-gray-50">
              {t("arrival_and_referral")}
            </p>
            <div className="grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Arrival time</p>
                <p className="font-medium text-gray-950 dark:text-gray-50">
                  {formatHospitalizationDateTime(
                    encounter.hospitalization.arrival_time,
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Admission time</p>
                <p className="font-medium text-gray-950 dark:text-gray-50">
                  {formatHospitalizationDateTime(
                    encounter.hospitalization.admission_time,
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{t("mode_of_arrival")}</p>
                <p className="font-medium text-gray-950 dark:text-gray-50">
                  {t(
                    `encounter_arrival_mode__${encounter.hospitalization.arrival_mode}`,
                  )}
                </p>
              </div>
              {encounter.hospitalization.arrival_mode === "referral" && (
                <>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {t("referred_by_practitioner")}
                    </p>
                    <p className="font-medium text-gray-950 dark:text-gray-50">
                      {encounter.hospitalization.referred_by_practitioner ||
                        "--"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {t("referred_by_facility")}
                    </p>
                    <p className="font-medium text-gray-950 dark:text-gray-50">
                      {encounter.hospitalization.referred_by_facility || "--"}
                    </p>
                  </div>
                </>
              )}
              {referralLetters?.results[0] && (
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {t("referral_letter")}
                  </p>
                  <p className="flex items-center gap-1 font-medium text-gray-950 dark:text-gray-50">
                    <Paperclip className="size-3.5" />
                    <span className="truncate">
                      {referralLetters.results[0].name}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-between items-center">
          <span className="text-gray-950 font-semibold dark:text-gray-50">
            {t("hospitalisation")}
          </span>
          {encounter.hospitalization?.re_admission && (
            <Badge variant="blue">{t("re_admission")}</Badge>
          )}
        </div>
        <div className="flex flex-row gap-2 bg-gray-100 rounded-md border border-gray-200 dark:bg-gray-900 dark:border-gray-800">
          <div className="flex flex-col p-2">
            <span className="text-sm">{t("admission_source")}</span>
            <span className="text-sm text-black font-semibold">
              {t(
                encounter.hospitalization?.admit_source
                  ? `encounter_admit_sources__${encounter.hospitalization?.admit_source}`
                  : "--",
              )}
            </span>
          </div>
          <div className="flex flex-col p-2">
            <span className="text-sm">{t("diet_preference")}</span>
            <span className="text-sm text-black font-semibold">
              {t(
                encounter.hospitalization?.diet_preference
                  ? `encounter_diet_preference__${encounter.hospitalization?.diet_preference}`
                  : "--",
              )}
            </span>
          </div>
          <div className="flex flex-col p-2">
            <span className="text-sm">Expected discharge date</span>
            <span className="text-sm text-black font-semibold">
              {encounter.hospitalization?.expected_discharge_date || "--"}
            </span>
          </div>
          <div className="flex flex-col p-2">
            <span className="text-sm">Medico-Legal Case</span>
            <span className="text-sm text-black font-semibold">
              {encounter.hospitalization?.medico_legal_case ? "Yes" : "No"}
            </span>
          </div>
        </div>
      </div>
      {isInpatient && (
        <HospitalizationDetailsDialog
          encounter={encounter}
          open={arrivalDialogOpen}
          onOpenChange={setArrivalDialogOpen}
        />
      )}
    </div>
  );
};
